import { cn } from '@pm/ui';
import type { ReactNode } from 'react';

/** Barras horizontais em CSS: sem dependência, respeita tokens e densidade. */
export function BarList({
  items,
  empty = 'Sem dados no período.',
  color = 'var(--accent)',
  onSelect,
}: {
  items: { id: string | null; name: string; count: number }[];
  empty?: string;
  color?: string;
  onSelect?: (item: { id: string | null; name: string }) => void;
}) {
  const max = Math.max(1, ...items.map((i) => i.count));
  if (items.length === 0) return <p className="py-6 text-center text-sm text-text-3">{empty}</p>;
  return (
    <ul className="flex flex-col gap-1.5">
      {items.map((it) => (
        <li
          key={it.id ?? it.name}
          className="grid grid-cols-[minmax(0,140px)_1fr_auto] items-center gap-2 text-sm"
        >
          <button
            type="button"
            disabled={!onSelect}
            onClick={() => onSelect?.(it)}
            className={cn('truncate text-left text-text-2', onSelect && 'hover:text-text-1')}
            title={it.name}
          >
            {it.name}
          </button>
          <div className="h-2 overflow-hidden rounded-sm bg-surface-2">
            <div
              className="h-full rounded-sm transition-[width] duration-(--dur-base)"
              style={{ width: `${(it.count / max) * 100}%`, background: color }}
            />
          </div>
          <span className="w-8 text-right font-mono text-xs text-text-2">{it.count}</span>
        </li>
      ))}
    </ul>
  );
}

/** Colunas verticais (SEG..SEX/DOM). */
export function ColumnChart({
  items,
  onSelect,
  active,
}: {
  items: { key: string; label: string; count: number; muted?: boolean; danger?: number }[];
  onSelect?: (key: string) => void;
  active?: string;
}) {
  const max = Math.max(1, ...items.map((i) => i.count));
  return (
    <div
      className="grid h-[140px] gap-2"
      style={{ gridTemplateColumns: `repeat(${items.length}, minmax(0, 1fr))` }}
    >
      {items.map((it) => (
        <button
          key={it.key}
          type="button"
          disabled={!onSelect}
          onClick={() => onSelect?.(it.key)}
          className={cn(
            'group flex flex-col items-center justify-end gap-1 rounded px-1 transition-colors',
            onSelect && 'hover:bg-surface-2',
            active === it.key && 'bg-surface-2',
          )}
        >
          <span className="font-mono text-xs text-text-2">{it.count}</span>
          <div
            className="relative w-full max-w-[40px] overflow-hidden rounded-sm bg-surface-2"
            style={{ height: `${Math.max(4, (it.count / max) * 90)}px` }}
          >
            <div
              className="absolute inset-0 rounded-sm"
              style={{
                background: it.muted ? 'var(--text-3)' : 'var(--accent)',
                opacity: it.muted ? 0.5 : 1,
              }}
            />
            {it.danger ? (
              <div
                className="absolute inset-x-0 bottom-0 rounded-sm bg-danger"
                style={{ height: `${(it.danger / Math.max(1, it.count)) * 100}%` }}
              />
            ) : null}
          </div>
          <span
            className={cn(
              'font-mono text-[10px] uppercase tracking-[var(--tracking-caps)]',
              it.muted ? 'text-text-3' : 'text-text-2',
            )}
          >
            {it.label}
          </span>
        </button>
      ))}
    </div>
  );
}

export function StatCard({
  label,
  value,
  hint,
  tone,
  onClick,
}: {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  tone?: 'danger' | 'warning' | 'success' | 'accent';
  onClick?: () => void;
}) {
  const Comp = onClick ? 'button' : 'div';
  return (
    <Comp
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      className={cn(
        'flex flex-col gap-1 rounded-md border border-border bg-surface-1 p-3 text-left',
        onClick && 'transition-colors hover:border-border-strong',
      )}
    >
      <span className="font-mono text-xs uppercase tracking-[var(--tracking-caps)] text-text-3">
        {label}
      </span>
      <span
        className={cn(
          'font-mono text-2xl leading-none',
          tone === 'danger' && 'text-danger',
          tone === 'warning' && 'text-warning',
          tone === 'success' && 'text-success',
          tone === 'accent' && 'text-accent',
        )}
      >
        {value}
      </span>
      {hint ? <span className="text-xs text-text-3">{hint}</span> : null}
    </Comp>
  );
}

export function Panel({
  title,
  children,
  action,
}: {
  title: string;
  children: ReactNode;
  action?: ReactNode;
}) {
  return (
    <section className="flex flex-col gap-3 rounded-md border border-border bg-surface-1 p-4">
      <header className="flex items-center gap-2">
        <span className="font-mono text-xs uppercase tracking-[var(--tracking-caps)] text-text-3">
          {title}
        </span>
        {action ? <span className="ml-auto">{action}</span> : null}
      </header>
      {children}
    </section>
  );
}
